import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import WorkHistoryOutlinedIcon from "@mui/icons-material/WorkHistoryOutlined";
import PersonAddAlt1OutlinedIcon from "@mui/icons-material/PersonAddAlt1Outlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import Comment from "./Comment";
import React, { useState } from "react";
import axios from "axios";
import Cookies from "js-cookie";
import ModalLoadingOverlay, {
  modalLoadingContainerSx,
} from "./ModalLoadingOverlay";

const CandidateActivities = ({
  candidatedetails,
  selectedRowData,
  setrefreshagain,
  refreshagain,
  activitesrefresh,
  setactivitesrefresh,
  setnewvalue,
  setstatusupdate,
  selectedStatus,
  handleSnackbarOpen,
}) => {
  const [showComment, setShowComment] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [editedComment, setEditedComment] = useState("");
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [submittingAction, setSubmittingAction] = useState(false);
  const activities = candidatedetails.activities;

  // Group all activities by date
  const activitiesByDate =
    activities?.reduce((acc, task) => {
      const dateKey = new Date(task.date_created).toLocaleDateString();
      if (!acc[dateKey]) acc[dateKey] = [];
      acc[dateKey].push(task);
      return acc;
    }, {}) || {};

  const getColor = (notes) => {
    if (notes?.includes("applied")) return "#2196F3";
    if (notes?.includes("created")) return "#FFA93C";
    return "#4CAF50";
  };

  const getIcon = (notes) => {
    if (notes?.includes("applied")) {
      return (
        <Box
          className="icon-container"
          sx={{
            border: "1px solid rgba(33, 150, 243, 0.20)",
            borderRadius: "10px",
            height: "60px",
            minWidth: "60px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            transition: "background-color 0.5s",
          }}
        >
          <PersonOutlineOutlinedIcon
            className="icon"
            style={{ color: "#979797" }}
          />
        </Box>
      );
    } else if (notes?.includes("created")) {
      return (
        <Box
          className="icon-container"
          sx={{
            border: "1px solid rgba(255, 169, 60, 0.20)",
            borderRadius: "10px",
            height: "60px",
            minWidth: "60px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            transition: "background-color 0.5s",
          }}
        >
          <PersonAddAlt1OutlinedIcon
            className="icon"
            style={{ color: "#979797" }}
          />
        </Box>
      );
    } else {
      return (
        <Box
          className="icon-container"
          sx={{
            border: "1px solid rgba(76, 175, 80, 0.20)",
            borderRadius: "10px",
            height: "60px",
            minWidth: "60px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            transition: "background-color 0.5s",
          }}
        >
          <WorkHistoryOutlinedIcon
            className="icon"
            style={{ color: "#979797" }}
          />
        </Box>
      );
    }
  };

  // Format date as DD-MM-YYYY like ShortlistedDialog
  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    return `${dd}-${mm}-${yyyy}`;
  };

  const refreshActivities = () => {
    setrefreshagain(!refreshagain);
    setactivitesrefresh(!activitesrefresh);
  };

  const handleEditOpen = (item) => {
    setSelectedActivity(item);
    setEditedComment(item?.notes || "");
    setEditDialogOpen(true);
  };

  const handleEditClose = () => {
    setEditDialogOpen(false);
    setSelectedActivity(null);
    setEditedComment("");
  };

  const handleDeleteOpen = (item) => {
    setSelectedActivity(item);
    setDeleteDialogOpen(true);
  };

  const handleDeleteClose = () => {
    setDeleteDialogOpen(false);
    setSelectedActivity(null);
  };

  const handleEditSave = async () => {
    if (!selectedActivity?.activity_id || !editedComment.trim()) return;
    setSubmittingAction(true);
    try {
      await axios.post(
        "/edit-comment",
        { activity_id: selectedActivity.activity_id, comment: editedComment },
        {
          headers: {
            authorization:
              Cookies.get("_secure_ARJ_") || Cookies.get("_secure_ref"),
          },
        },
      );
      handleSnackbarOpen("Comment updated successfully");
      refreshActivities();
      handleEditClose();
    } catch (error) {
      handleSnackbarOpen(
        error?.response?.data?.error || "Failed to update comment",
      );
    } finally {
      setSubmittingAction(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!selectedActivity?.activity_id) return;
    setSubmittingAction(true);
    try {
      await axios.post(
        "/delete-comment",
        { activity_id: selectedActivity.activity_id },
        {
          headers: {
            authorization:
              Cookies.get("_secure_ARJ_") || Cookies.get("_secure_ref"),
          },
        },
      );
      handleSnackbarOpen("Comment deleted successfully");
      refreshActivities();
      handleDeleteClose();
    } catch (error) {
      handleSnackbarOpen(
        error?.response?.data?.error || "Failed to delete comment",
      );
    } finally {
      setSubmittingAction(false);
    }
  };

  const editButtonSx = {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    px: "10px",
    height: "34px",
    border: "1px solid #E2E8F0",
    borderRadius: "8px",
    cursor: "pointer",
    backgroundColor: "#fff",
    flexShrink: 0,
    "&:hover": { backgroundColor: "#F8FAFC" },
  };

  const deleteButtonSx = {
    width: "34px",
    height: "34px",
    border: "1px solid #FECACA",
    borderRadius: "8px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    backgroundColor: "#fff",
    flexShrink: 0,
    "&:hover": { backgroundColor: "#FFF5F5" },
  };

  const ActivityItem = ({ item }) => (
    <Box
      sx={{
        "&:hover .icon-container": { backgroundColor: getColor(item.notes) },
        "&:hover .icon": { color: "white !important" },
        "&:hover .activity-logs": { fontWeight: "500 !important" },
        display: "flex",
        alignItems: "flex-start",
        gap: "14px",
        mb: 2,
      }}
    >
      {getIcon(item.notes)}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 1.5,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{ fontWeight: 400, fontSize: "10px", color: "#737791" }}
            >
              {formatDate(item.date_created)}
            </Typography>
            <Typography
              className="activity-logs"
              sx={{
                textOverflow: "ellipsis",
                overflow: "hidden",
                fontSize: "14px",
                color: "#151D48",
              }}
            >
              {item.entered_by}
            </Typography>
          </Box>

          <Stack direction="row" spacing={0.75} sx={{ flexShrink: 0 }}>
            
              <Box sx={editButtonSx} onClick={() => handleEditOpen(item)}>
                <img src="/assets/edit.svg" alt="" />
                <Typography
                  sx={{ fontSize: "13px", fontWeight: 600, color: "#3F95EA" }}
                >
                  Edit
                </Typography>
              </Box>
              <Box sx={deleteButtonSx} onClick={() => handleDeleteOpen(item)}>
                <img src="/assets/delete.svg" alt="" />
              </Box>
          </Stack>
        </Box>

        {item.notes.split("\n").map((line, i) => (
          <Typography
            key={i}
            className="activity-logs"
            sx={{
              textOverflow: "ellipsis",
              overflow: "hidden",
              fontSize: "14px",
              color: "#151D48",
            }}
          >
            {line}
          </Typography>
        ))}
      </Box>
    </Box>
  );

  return (
    <Box sx={{ py: 1 }}>
      {/* Write a Comment button */}
      <Comment
        setShowComment={setShowComment}
        handleSnackbarOpen={handleSnackbarOpen}
        selectedStatus={selectedStatus}
        setstatusupdate={setstatusupdate}
        setnewvalue={setnewvalue}
        setactivitesrefresh={setactivitesrefresh}
        activitesrefresh={activitesrefresh}
        refreshagain={refreshagain}
        setrefreshagain={setrefreshagain}
        selectedRowData={selectedRowData}
      />
      <Box sx={{ mt: 2 }}>
        {activities?.map((item, idx) => (
          <ActivityItem key={idx} item={item} />
        ))}
      </Box>

      <Dialog
        open={editDialogOpen}
        onClose={handleEditClose}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: modalLoadingContainerSx(submittingAction) }}
      >
        <DialogTitle>Edit Comment</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Comment"
            value={editedComment}
            inputProps={{ maxLength: 500 }}
            onChange={(event) => setEditedComment(event.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button className="cancel-btn" onClick={handleEditClose}>
            Cancel
          </Button>
          <Button onClick={handleEditSave} disabled={!editedComment.trim()}>
            Save
          </Button>
        </DialogActions>
        <ModalLoadingOverlay open={submittingAction} />
      </Dialog>

      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteClose}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: modalLoadingContainerSx(submittingAction) }}
      >
        <DialogTitle>Delete Comment</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this comment?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button className="cancel-btn" onClick={handleDeleteClose}>
            Cancel
          </Button>
          <Button color="error" onClick={handleDeleteConfirm}>
            Delete
          </Button>
        </DialogActions>
        <ModalLoadingOverlay open={submittingAction} />
      </Dialog>
    </Box>
  );
};

export default CandidateActivities;
